import { useEffect, type ReactNode } from "react"
import { usePage } from "@inertiajs/react"
import PublicNav from "@/components/PublicNav"
import PublicFooter from "@/components/PublicFooter"
import GiselaNav from "@/components/gisela/GiselaNav"
import GiselaFooter from "@/components/gisela/GiselaFooter"
import { AssistantWidget } from "@/components/assistant/AssistantWidget"
import { LanguageOffer } from "@/components/LanguageOffer"
import { AdminBar, adminBarOffset } from "@/components/AdminBar"
import { trackConversion } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

interface Props {
  children: ReactNode
  /** The page opens on a dark, full-bleed hero, so the nav sits on it in
   *  white until the visitor scrolls past. Gisela's nav is solid cream
   *  otherwise; the venue's nav does this on every page of its own accord. */
  darkHero?: boolean
}

// One layout, two brands: the chrome follows the requested host (current_brand
// shared prop), so shared pages (articles, privacy) automatically wear the
// right nav/footer on each site.
export default function PublicLayout({ children, darkHero = false }: Props) {
  const { current_brand, admin_bar } = usePage<PageProps>().props
  const gisela = current_brand.key === "gisela"

  // The public sites are light-only (the bands paint cream/stone/white that
  // never switch). The layout skips the theme boot script here, but an admin
  // arriving by Inertia visit from the portal may still carry `.dark` on
  // <html>; drop it so the text tokens can't turn pale on a pale background.
  useEffect(() => {
    document.documentElement.classList.remove("dark")
  }, [])

  // Count phone/email taps anywhere on the public site for the admin funnel.
  // Capture phase so the event fires even when the tap navigates away.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target instanceof Element ? e.target : null
      const anchor = target?.closest("a[href^='tel:'], a[href^='mailto:']")
      if (!anchor) return
      const href = anchor.getAttribute("href") ?? ""
      trackConversion(href.startsWith("tel:") ? "call_click" : "email_click")
    }
    document.addEventListener("click", onClick, true)
    return () => document.removeEventListener("click", onClick, true)
  }, [])

  return (
    <div className="flex min-h-screen flex-col" style={adminBarOffset(Boolean(admin_bar))}>
      <AdminBar variant="public" />
      {gisela ? <GiselaNav overHero={darkHero} /> : <PublicNav />}
      <main className="flex-1">{children}</main>
      {gisela ? <GiselaFooter /> : <PublicFooter />}
      <AssistantWidget />
      <LanguageOffer />
    </div>
  )
}
